/* global chrome */

import React, { Fragment, Component } from 'react'
import Privacy from './components/privacy';
import Terms from './components/terms';
import Refund from './components/refund';
import Extension from './components/extension';
import AppBar from './components/appBar';

import Home from 'components/home';
import Search from 'components/search';
import Newsfeed from 'components/newsfeed';
import Activity from 'components/activity';
import LibraryCanvasPage from 'components/library/LibraryCanvasPage';
import PureTldrawCanvas from 'canvas/components/PureTldrawCanvas';
import Settings from 'components/settings';
import Premium from 'components/premium';
import Pricing from 'components/pricing';

import SignIn from 'components/home/signIn';
import SignInScreen from 'components/signInScreen';
import Uncompleted from 'components/settings/uncompleted';
import Welcome from 'components/welcome';
import Admin from 'components/admin';
import { ThemeProvider, StyledEngineProvider, createTheme, adaptV4Theme } from '@mui/material/styles';

import LoadingScreen from 'components/loadingScreen';

import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Provider, useSelector, useDispatch } from 'react-redux';
import { createStore, applyMiddleware } from 'redux';
import createSagaMiddleware from 'redux-saga';
import rootReducer from './reducers';
import { createLogger } from 'redux-logger';
import rootSaga from './sagas';

import demoUserJson from 'components/newsfeed/demoUser.json';

import socket from 'socket';

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
} from "react-router-dom";

const sagaMiddleware = createSagaMiddleware();
const logger = createLogger();


let middleware = [];

if (process.env.NODE_ENV === 'development') {
  middleware = [...middleware, sagaMiddleware, logger];
} else {
  middleware = [...middleware, sagaMiddleware, logger];
}
const store = createStore(
	rootReducer,
  applyMiddleware(...middleware),
)

sagaMiddleware.run(rootSaga);


const Loader = props => {
  const dispatch = useDispatch();
  const user = useSelector(state => state.user);
  const { extensionId, extensionInstalled, userLoaded } = useSelector(state => state.api);
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    dispatch({ type: 'SET_USER', method: 'get', path:'/auth/user' + '?nocache=' + new Date().getTime() })
    //dispatch({type: 'SET_DEMO_USER', demoUserJson })
    const isChrome = !!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime);
    if(isChrome){
      dispatch({type: 'IS_CHROME'})
      console.log('sending the version request to extension')
      if (!chrome.runtime || !chrome.runtime.sendMessage){
        dispatch({type: "EXTENSION_NOT_INSTALLED"});
        console.log('Extension is not installed');
        return;
      }

      chrome.runtime.sendMessage(extensionId, {type: 'VERSION'}, function(response){
        if (chrome.runtime.lastError) {
          console.log('Extension handshake failed:', chrome.runtime.lastError.message);
          dispatch({type: "EXTENSION_NOT_INSTALLED"});
          return;
        }
        if (response && response.success){
          dispatch({type: "EXTENSION_INSTALLED"});
          console.log('Extension is installed');
        } else {
          dispatch({type: "EXTENSION_NOT_INSTALLED"});
          console.log('Extension is not installed');
        }
      });
    }
  },
  [])
 

  React.useEffect(() => {
    if (!userLoaded) return undefined;
    const el = document.getElementById('initial-loader');
    if (!el) return undefined;
    el.classList.add('is-leaving');
    const timer = window.setTimeout(() => el.remove(), 280);
    return () => window.clearTimeout(timer);
  }, [userLoaded]);

  React.useEffect(() => {
    const path = location.pathname;

    const staticPaths = new Set([
      '/privacy',
      '/terms',
      '/refund',
      '/admin',
      '/newsfeed',
      '/search',
      '/settings',
      '/premium',
      '/pricing',
      '/en',
      '/kr',
    ]);
    if (staticPaths.has(path)) return;
    if (path.startsWith('/extension/')) return;
    // /:person_name/:keyword and deeper — handled by Activity/Canvas, not Loader
    if (path !== '/' && path.substring(1).includes('/')) return;

    if (!userLoaded) return;

    if (!user) {
      if (path === '/') navigate('/en');
      return;
    }

    // Logged-in: only root → own profile (not /en, not /otherusername)
    if (user.username && path === '/') {
      navigate(`/${user.username}`);
    }
  }, [user, userLoaded, location.pathname, navigate]);

  if (!userLoaded) {
    return <LoadingScreen />;
  }

  return null;
}


const BoardPage = () => {
  const location = useLocation();
  const isGallery = new URLSearchParams(location.search).get('view') === 'gallery';
  return isGallery ? <Activity /> : <LibraryCanvasPage />;
};

const LegacyCanvasRedirect = () => {
  const { person_name, keyword } = useParams();
  const location = useLocation();
  return (
    <Navigate
      to={`/${encodeURIComponent(person_name)}/${encodeURIComponent(keyword)}${location.search}${location.hash}`}
      replace
    />
  );
};

const SignedIn = props => {
  const dispatch = useDispatch();
  const user = useSelector(state => state.user);
  const { extensionId, extensionInstalled, userLoaded } = useSelector(state => state.api);

  const navigate = useNavigate();

  React.useEffect(() => {

    if(!userLoaded) return;
    if(user?.username){
      navigate(`/${user.username}`);
      return;
    }

  }, [user, userLoaded])

  if (!userLoaded) {
    return <LoadingScreen />;
  }

  return null;
}

const theme = createTheme(adaptV4Theme({
  palette: {
    white: '#FFFFFF',
    text:{
      primary: '#000000',
      secondary: '#676160',
      contrast: '#FFFFFF',
    }
  },
  breakpoints: {
    values: {

      xs: 0,
      sm: 600,
      md: 900,
      lg: 1400,
      xl: 1700,

      ms_1: 0,
      ms_2: 600,
      ms_3: 1200,
      ms_4: 1800,
      ms_5: 2400,
    },
  },
  typography: {
    fontFamily: [
      '"Inter"',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      // '"Helvetica Neue"',
      // 'Arial',
      'sans-serif',
      // '"Apple Color Emoji"',
      // '"Segoe UI Emoji"',
      // '"Segoe UI Symbol"',
    ].join(','),
  },
}));

const App = props => {
    return (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <Provider store={store}>
            <Router>
              <Loader/>
              <AppBar/>
              {/*
              <Dialogs/>

                <Route path='/suggest' element={Suggest}/>
              */
              }
              <Routes>
                <Route exact path='/admin' element={<Admin/>}/>
                <Route exact path='/premium' element={<Premium/>}/>
                <Route exact path='/pricing' element={<Pricing/>}/>
                <Route path='/welcome' element={<Navigate to='/extension/welcome' replace />}/>
                <Route path='/extension/welcome' element={<Welcome mode="create-canvas"/>}/>
                <Route exact path='/extension/signed_in' element={<SignedIn/>}/>
                <Route exact path='/extension/signin' element={<SignInScreen/>}/>
                <Route exact path='/extension/uncompleted' element={<Uncompleted/>}/>
                <Route exact path='/settings' element={<Settings/>}/>
                <Route exact path='/privacy' element={<Privacy/>}/>
                <Route exact path='/terms' element={<Terms/>}/>
                <Route exact path='/refund' element={<Refund/>}/>
                <Route exact path='/search' element={<Search/>}/>
                <Route exact path='/newsfeed' element={<Newsfeed/>}/>
                <Route exact path='/en' element={<Home/>}/>
                <Route exact path='/kr' element={<Home/>}/>
                <Route exact path='/tldraw-test' element={<PureTldrawCanvas/>}/>
                <Route exact path='/:person_name' element={<Activity/>}/>
                <Route exact path='/:person_name/:keyword' element={<BoardPage/>}/>
                <Route exact path='/:person_name/:keyword/canvas' element={<LegacyCanvasRedirect/>}/>
                <Route path={'/'} render={() =>
                  <Fragment>
                    <Home />
                  </Fragment>
                }
                />
              </Routes>
            </Router>
          </Provider>
        </ThemeProvider>
      </StyledEngineProvider>
    );
}

export default App;
